import { TimeSlot, DaySchedule, WeeklySchedule, EmployeeStatusEnum } from './types';

export const generateUniqueId = (): string => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

export const calculateHoursFromTimeSlots = (timeSlots: TimeSlot[]): number => {
    return timeSlots.reduce((total, slot) => {
        const startMinutes = timeToMinutes(slot.startTime);
        const endMinutes = timeToMinutes(slot.endTime);
        const durationMinutes = endMinutes - startMinutes;
        return total + (durationMinutes / 60);
    }, 0);
};

export const timeToMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
};

export const minutesToTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

export const formatHours = (hours: number): string => {
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    
    if (minutes === 0) {
        return `${wholeHours}h`;
    } else {
        return `${wholeHours}h${minutes.toString().padStart(2, '0')}`;
    }
};

export const calculateDailyHours = (daySchedule: DaySchedule): number => {
    if (daySchedule.status !== EmployeeStatusEnum.PRESENT) {
        return 0;
    }
    return calculateHoursFromTimeSlots(daySchedule.timeSlots);
};

export const calculateWeeklyHours = (schedule: WeeklySchedule): number => {
    return Object.values(schedule.days).reduce((total, day) => {
        return total + calculateDailyHours(day);
    }, 0);
};

export const validateTimeSlots = (timeSlots: TimeSlot[]): { isValid: boolean; error?: string } => {
    if (timeSlots.length === 0) {
        return { isValid: false, error: 'Au moins un créneau est requis' };
    }

    // Sort time slots by start time
    const sortedSlots = [...timeSlots].sort((a, b) => 
        timeToMinutes(a.startTime) - timeToMinutes(b.startTime)
    );

    for (let i = 0; i < sortedSlots.length; i++) {
        const slot = sortedSlots[i];
        const startMinutes = timeToMinutes(slot.startTime);
        const endMinutes = timeToMinutes(slot.endTime);

        // Check if start time is before end time
        if (startMinutes >= endMinutes) {
            return { isValid: false, error: 'L\'heure de fin doit être après l\'heure de début' };
        }

        // Check minimum duration (30 minutes)
        if (endMinutes - startMinutes < 30) {
            return { isValid: false, error: 'La durée minimum d\'un créneau est de 30 minutes' };
        }

        // Check for overlaps with next slot
        if (i < sortedSlots.length - 1) {
            const nextSlot = sortedSlots[i + 1];
            const nextStartMinutes = timeToMinutes(nextSlot.startTime);
            
            if (endMinutes > nextStartMinutes) {
                return { isValid: false, error: 'Les créneaux ne peuvent pas se chevaucher' };
            }
        }
    }

    return { isValid: true };
};